import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { Permission, hasPermission } from "../../types/roles";
import Tooltip from "./Tooltip";
import NotificationBell from "../notifications/NotificationBell";
import SearchBar from "./SearchBar";
import VersionBadge from "./VersionBadge";
import WorkshopSelector from "../WorkshopSelector";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Cargar estado del sidebar desde localStorage
    const saved = localStorage.getItem("sidebarOpen");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isMobile, setIsMobile] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [settingsButtonPosition, setSettingsButtonPosition] = useState({
    top: 0,
    left: 0,
  });
  const userMenuRef = useRef<HTMLDivElement>(null);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  // Guardar estado del sidebar en localStorage cuando cambie
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem("sidebarOpen", JSON.stringify(sidebarOpen));
    }
  }, [sidebarOpen, isMobile]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Detect mobile and adjust sidebar
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Cerrar menús al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
      if (
        settingsMenuRef.current &&
        !settingsMenuRef.current.contains(event.target as Node)
      ) {
        setSettingsMenuOpen(false);
      }
    };

    if (userMenuOpen || settingsMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuOpen, settingsMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/dashboard") return "Dashboard";
    if (path === "/vehicles") return "Vehicles";
    if (path === "/inspections") return "Inspections";
    if (path === "/inspections/new") return "New Inspection";
    if (path === "/inspections-board") return "Inspection Board";
    if (path === "/notifications") return "Notifications";
    if (path === "/users") return "Users";
    if (path === "/settings") return "Settings";
    if (path === "/settings/profile") return "Profile Settings";
    if (path === "/admin/users") return "User Management";
    if (path === "/admin/workshops") return "Workshop Management";
    if (path === "/admin/ip-access") return "IP Access Control";
    if (path === "/admin/device-tokens") return "Device Token Management";
    if (path.startsWith("/inspections/edit/")) return "Edit Inspection";
    if (path.startsWith("/inspections/")) return "Inspection Details";
    return "Dashboard";
  };

  // Get user role for permission checks
  const userRole = user ? (user.is_admin ? "admin" : "inspector") : undefined;
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Define menu items - Common for all users
  const commonMenuItems = [
    {
      name: "Home",
      path: "/dashboard",
      permission: Permission.VIEW_DASHBOARD,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
    },
    {
      name: "Inspection Board",
      path: "/inspections-board",
      permission: Permission.VIEW_INSPECTION_BOARD,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
          />
        </svg>
      ),
    },
    {
      name: "All Inspections",
      path: "/inspections",
      permission: Permission.VIEW_INSPECTIONS,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
      ),
    },
    {
      name: "New Inspection",
      path: "/inspections/new",
      permission: Permission.CREATE_INSPECTION,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      name: "Vehicles",
      path: "/vehicles",
      permission: Permission.VIEW_VEHICLES,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
          />
        </svg>
      ),
    },
    {
      name: "Notifications",
      path: "/notifications",
      permission: Permission.VIEW_NOTIFICATIONS,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
      ),
    },
  ];

  // Settings submenu items (available for all users)
  const settingsItems: any[] = [];

  // Admin-only settings submenu items
  const adminSettingsItems = [
    {
      name: "Manage Users",
      path: "/admin/users",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
    },
    {
      name: "Manage Workshops",
      path: "/admin/workshops",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
    },
    {
      name: "Access Control",
      path: "/admin/ip-access",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      ),
    },
    {
      name: "Device Tokens",
      path: "/admin/device-tokens",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      ),
    },
  ];

  // Combine settings items based on role
  const allSettingsItems = user?.is_admin
    ? [...settingsItems, ...adminSettingsItems]
    : settingsItems;

  // Filter menu items based on user permissions
  const menuItems = commonMenuItems.filter((item) => {
    if (item.permission) {
      return hasPermission(userRole, item.permission);
    }
    return true;
  });

  return (
    <div
      className={`min-h-screen flex transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      {/* Sidebar */}
      <aside
        className={`shadow-lg fixed h-full z-50 transition-all duration-300 ease-in-out ${
          sidebarOpen ? "w-56" : "w-20"
        } ${isMobile && !sidebarOpen ? "-translate-x-full" : "translate-x-0"} ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header del sidebar */}
          <div
            className={`flex items-center ${
              sidebarOpen
                ? "px-5 py-4 justify-between"
                : "px-2 py-4 justify-center w-full"
            } ${
              isDarkMode
                ? "border-b border-gray-700"
                : "border-b border-gray-200"
            }`}
          >
            {sidebarOpen ? (
              <>
                <div>
                  <h1
                    className={`text-lg font-bold ${
                      isDarkMode ? "text-red-400" : "text-red-600"
                    }`}
                  >
                    Inspection Board
                  </h1>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className={`p-1 rounded-lg transition-colors ${
                    isDarkMode
                      ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                    />
                  </svg>
                </button>
              </>
            ) : (
              <button
                onClick={() => setSidebarOpen(true)}
                className={`p-1 rounded-lg transition-colors w-full flex justify-center ${
                  isDarkMode
                    ? "text-red-400 hover:text-red-300 hover:bg-gray-700"
                    : "text-red-600 hover:text-red-700 hover:bg-red-50"
                }`}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 5l7 7-7 7M5 5l7 7-7 7"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Navegación */}
          <nav
            className={`flex-1 py-4 space-y-1 overflow-y-auto ${
              sidebarOpen ? "px-3" : "flex flex-col items-center px-2"
            }`}
          >
            {/* Common menu items */}
            {menuItems.map((item) =>
              !sidebarOpen ? (
                <Tooltip key={item.path} content={item.name} position="right">
                  <Link
                    to={item.path}
                    onClick={() => isMobile && setSidebarOpen(false)}
                    className={`flex items-center justify-center rounded-lg transition-all duration-200 p-3 ${
                      isActive(item.path)
                        ? isDarkMode
                          ? "bg-red-900/40 text-red-400 font-medium shadow-lg"
                          : "bg-red-600 text-white font-medium shadow-lg"
                        : isDarkMode
                        ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                    style={{ width: "48px", height: "48px" }}
                  >
                    {item.icon}
                  </Link>
                </Tooltip>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => isMobile && setSidebarOpen(false)}
                  className={`flex items-center rounded-lg transition-all duration-200 space-x-3 px-4 py-3.5 group ${
                    isActive(item.path)
                      ? isDarkMode
                        ? "bg-red-900/40 text-red-400 font-medium shadow-lg"
                        : "bg-red-600 text-white font-medium shadow-lg"
                      : isDarkMode
                      ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <span className="text-[15px] font-medium">{item.name}</span>
                </Link>
              )
            )}

            {/* Settings section - Only show if user has settings items (admin only) */}
            {sidebarOpen && allSettingsItems.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className={`w-full flex items-center rounded-lg transition-all duration-200 space-x-3 px-4 py-3 group ${
                    isDarkMode
                      ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  <svg
                    className="w-6 h-6 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="flex-1 text-left text-[15px] font-medium">
                    Admin Settings
                  </span>
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${
                      settingsOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Settings submenu items */}
                {settingsOpen && (
                  <div className="mt-2 space-y-1">
                    {allSettingsItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => isMobile && setSidebarOpen(false)}
                        className={`flex items-center rounded-lg transition-all duration-200 space-x-3 pl-12 pr-4 py-3 group ${
                          isActive(item.path)
                            ? isDarkMode
                              ? "bg-red-900/40 text-red-400 font-medium"
                              : "bg-red-600 text-white font-medium"
                            : isDarkMode
                            ? "text-gray-400 hover:bg-gray-700 hover:text-gray-200"
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                        }`}
                      >
                        <span className="flex-shrink-0">{item.icon}</span>
                        <span className="text-sm font-medium">{item.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Settings icon when collapsed */}
            {!sidebarOpen && allSettingsItems.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Tooltip content="Admin Settings" position="right">
                  <button
                    ref={settingsButtonRef}
                    onClick={() => {
                      if (settingsButtonRef.current) {
                        const rect =
                          settingsButtonRef.current.getBoundingClientRect();
                        setSettingsButtonPosition({
                          top: rect.top,
                          left: rect.right + 8,
                        });
                      }
                      setSettingsMenuOpen(!settingsMenuOpen);
                    }}
                    className={`flex items-center justify-center rounded-lg transition-all duration-200 p-3 ${
                      settingsMenuOpen
                        ? isDarkMode
                          ? "bg-gray-700 text-white"
                          : "bg-gray-100 text-gray-900"
                        : isDarkMode
                        ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                    style={{ width: "48px", height: "48px" }}
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </button>
                </Tooltip>
              </div>
            )}
          </nav>

          {/* Usuario */}
          <div
            className={`${sidebarOpen ? "p-3" : "p-2"} ${
              isDarkMode
                ? "border-t border-gray-700"
                : "border-t border-gray-200"
            }`}
          >
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className={`flex items-center w-full rounded-lg transition-all duration-200 ${
                  sidebarOpen ? "space-x-3 px-3 py-3" : "justify-center p-2.5"
                } ${isDarkMode ? "hover:bg-gray-700/70" : "hover:bg-gray-100"}`}
                title={!sidebarOpen ? user?.username || "User" : ""}
              >
                <div
                  className={`flex items-center justify-center text-white font-bold shadow-lg ring-2 ${
                    isDarkMode
                      ? "bg-gradient-to-br from-blue-500 to-blue-600 ring-blue-400/30"
                      : "bg-gradient-to-br from-blue-600 to-blue-700 ring-blue-500/30"
                  }`}
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    minWidth: "40px",
                    minHeight: "40px",
                    fontSize: "16px",
                  }}
                >
                  {user?.username?.charAt(0).toUpperCase() || "U"}
                </div>
                {sidebarOpen && (
                  <>
                    <div className="flex-1 text-left min-w-0 overflow-hidden">
                      <p
                        className={`text-sm font-semibold truncate ${
                          isDarkMode ? "text-gray-100" : "text-gray-900"
                        }`}
                        title={user?.full_name || user?.username}
                      >
                        {user?.full_name || user?.username}
                      </p>
                      <p
                        className={`text-xs truncate ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                        title={user?.email}
                      >
                        {user?.email}
                      </p>
                    </div>
                    <svg
                      className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ${
                        userMenuOpen ? "rotate-180" : ""
                      } ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </>
                )}
              </button>

              {/* Menú desplegable cuando está expandido */}
              {userMenuOpen && sidebarOpen && (
                <div
                  className={`absolute bottom-full left-0 right-0 mb-2 rounded-xl shadow-2xl border overflow-hidden ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate("/settings/profile");
                    }}
                    title="My Profile"
                    className={`w-full px-4 py-3.5 text-left text-sm flex items-center space-x-3 transition-all duration-200 ${
                      isDarkMode
                        ? "text-gray-200 hover:bg-gray-600"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <svg
                      className="w-5 h-5 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    <span className="font-medium">My Profile</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    title="Sign Out"
                    className={`w-full px-4 py-3.5 text-left text-sm font-semibold flex items-center space-x-3 transition-all duration-200 ${
                      isDarkMode
                        ? "text-red-400 hover:bg-red-900/30 hover:text-red-300 border-t border-gray-600"
                        : "text-red-600 hover:bg-red-50 border-t border-gray-200"
                    }`}
                  >
                    <svg
                      className="w-5 h-5 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    <span>Sign Out</span>
                  </button>
                </div>
              )}

              {/* Menú desplegable cuando está colapsado */}
              {userMenuOpen && !sidebarOpen && (
                <div
                  className={`absolute bottom-full left-0 mb-2 rounded-xl shadow-2xl border overflow-hidden min-w-[220px] ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <div
                    className={`px-4 py-3 ${
                      isDarkMode
                        ? "border-b border-gray-600"
                        : "border-b border-gray-200"
                    }`}
                  >
                    <p
                      className={`text-sm font-semibold truncate ${
                        isDarkMode ? "text-gray-100" : "text-gray-900"
                      }`}
                      title={user?.full_name || user?.username}
                    >
                      {user?.full_name || user?.username}
                    </p>
                    <p
                      className={`text-xs truncate mt-0.5 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                      title={user?.email}
                    >
                      {user?.email}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate("/settings/profile");
                    }}
                    title="My Profile"
                    className={`w-full px-4 py-3.5 text-left text-sm flex items-center space-x-3 transition-all duration-200 ${
                      isDarkMode
                        ? "text-gray-200 hover:bg-gray-600"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <svg
                      className="w-5 h-5 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    <span className="font-medium">My Profile</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    title="Sign Out"
                    className={`w-full px-4 py-3.5 text-left text-sm font-semibold flex items-center space-x-3 transition-all duration-200 ${
                      isDarkMode
                        ? "text-red-400 hover:bg-red-900/30 hover:text-red-300 border-t border-gray-600"
                        : "text-red-600 hover:bg-red-50 border-t border-gray-200"
                    }`}
                  >
                    <svg
                      className="w-5 h-5 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Contenido principal */}
      <div
        className={`flex-1 transition-all duration-300 ${
          sidebarOpen ? "lg:ml-56" : "lg:ml-20"
        } ${isMobile ? "ml-0" : ""}`}
      >
        {/* Mobile overlay */}
        {isMobile && sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-45"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <header
          className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
            sidebarOpen && !isMobile ? "lg:ml-56" : !isMobile ? "lg:ml-20" : ""
          } ${
            isScrolled
              ? isDarkMode
                ? "bg-gray-800 shadow-md"
                : "bg-white shadow-md"
              : isDarkMode
              ? "bg-gray-900"
              : "bg-white"
          }`}
        >
          <div className="max-w-[1920px] mx-auto px-2 sm:px-4 lg:px-6 py-2 sm:py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Left side: Mobile menu OR Workshop Selector */}
              <div className="flex items-center gap-3 flex-shrink-0">
                {isMobile ? (
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className={`p-2 rounded-lg shadow-md flex-shrink-0 ${
                      isDarkMode
                        ? "bg-phoenix-600 text-white hover:bg-phoenix-700"
                        : "bg-phoenix-600 text-white hover:bg-phoenix-700"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={
                          sidebarOpen
                            ? "M6 18L18 6M6 6l12 12"
                            : "M4 6h16M4 12h16M4 18h16"
                        }
                      />
                    </svg>
                  </button>
                ) : null}

                {/* Workshop Selector */}
                <div className="flex-shrink-0">
                  <WorkshopSelector />
                </div>
              </div>

              {/* Center: Page Title */}
              <div className="hidden lg:block flex-shrink-0 absolute left-1/2 transform -translate-x-1/2">
                <h2
                  className={`text-xl font-bold whitespace-nowrap ${
                    isDarkMode ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  {getPageTitle()}
                </h2>
              </div>

              {/* Right side: Search, Notifications and Version Badge */}
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-auto">
                <SearchBar placeholder="Search..." compact={true} />
                <NotificationBell collapsed={isMobile} />
                <VersionBadge />
              </div>
            </div>
          </div>
        </header>

        {/* Spacer for fixed header */}
        <div className="h-14 sm:h-16"></div>

        <main
          className={`max-w-[1920px] mx-auto px-2 sm:px-4 lg:px-6 pb-6 sm:pb-8 ${
            isDarkMode ? "bg-gray-900" : "bg-gray-100"
          }`}
        >
          {children}
        </main>
      </div>

      {/* Portal para el menú flotante de configuración */}
      {settingsMenuOpen &&
        !sidebarOpen &&
        createPortal(
          <div
            ref={settingsMenuRef}
            className={`fixed w-64 rounded-xl shadow-2xl border overflow-hidden z-[9999] ${
              isDarkMode
                ? "bg-gray-700 border-gray-600"
                : "bg-white border-gray-200"
            }`}
            style={{
              top: `${settingsButtonPosition.top}px`,
              left: `${settingsButtonPosition.left}px`,
              animation: "slideIn 0.2s ease-out",
            }}
          >
            <div
              className={`px-3 py-2 border-b ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <p
                className={`text-xs font-semibold uppercase tracking-wide ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Admin Settings
              </p>
            </div>
            <div className="py-1">
              {allSettingsItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSettingsMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 text-sm transition-all duration-200 ${
                    isActive(item.path)
                      ? isDarkMode
                        ? "bg-gray-600 text-white"
                        : "bg-gray-100 text-gray-900"
                      : isDarkMode
                      ? "text-gray-300 hover:bg-gray-600 hover:text-white"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <span className="font-medium">{item.name}</span>
                </Link>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Layout;
